import BaranDataGrid from '../../common/BaranDataGrid.tsx';
import { ChangeEvent, useEffect, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import numberFormat from '../../../utils/numberFormat.ts';
import axios from 'axios';
import IResponse, { IPagination } from '../../../interfaces/response.ts';
import { useTranslation } from 'react-i18next';
import { MAX_PAGE_LIMIT } from '../../../constants/numbers.ts';
import { useCheckoutStatusColors } from '../../../enum/checkout.tsx';

interface Settlements {
	id: number;
	amount: number;
	status: string;
	user: string;
	card_number: string;
	created_at: string;
}

export const SettlementsDataGrid = () => {
	const { t } = useTranslation();

	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [loading, setLoading] = useState(false);
	const [fetchedData, setFetchedData] = useState<Settlements[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();

	const headerClassNames = 'text-xs bg-background-paper';

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const fetchCampaigns = () => {
		setLoading(true);
		axios<IResponse<Settlements[]>>({
			url: '/accounting-profile/settlements',
			params: {
				page: page,
				limit: pageSize,
				search: searchTerm,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const columns: GridColDef<Settlements>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'amount',
			headerName: t('amount'),
			width: 120,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => numberFormat(params.row?.amount),
		},
		{
			field: 'status',
			headerName: t('status'),
			width: 120,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const bgColorClass = useCheckoutStatusColors(params.value);
				return (
					<div className="flex h-full items-center justify-center">
						<div className={`flex w-[max-content] rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{t(params.value)}</div>
					</div>
				);
			},
		},
		{
			field: 'user',
			headerName: t('user'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'card_number',
			headerName: t('card_number'),
			width: 160,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'created_at',
			headerName: t('created_at'),
			width: 160,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return <>{new Date(params.row?.created_at ?? '').toLocaleDateString('fa-IR')}</>;
			},
		},
	];

	useEffect(() => {
		fetchCampaigns();
	}, [page, pageSize]);

	return (
		<div className={'w-full'}>
			<BaranDataGrid<Settlements> pagination={pagination} loading={loading} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} />;
		</div>
	);
};
